import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  booleanAttribute,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Select } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { Pagination } from '@app/shared/model/pagination.model';
import { ApiService } from '@core/providers/http';
import {
  BolsaService,
  BolseiroService,
  InstituicaoEnsinoService,
} from '../../../api/services';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { Subject, takeUntil } from 'rxjs';
import { toExtensoComMoeda } from '@app/core/helpers/toExtensoComMoeda';

interface VerificarRegraPagamentoPayload {
  id: string;
  pedidoID: number;
  bolseiroID: number;
  requerenteID: number;
  isActive: boolean;
  tipoBolsa: number;
  paisID: number;
  instituicaoGestoraID: number;
  nivelAcademico: number;
  areaProgramaFormacaoID: number;
  programaFormacaoID: number;
  anoCurricularFrequencia: string;
  valorSubsidio: number;
}

@Component({
  selector: 'app-memorando-apoio-defesa-monografia',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    Select,
    InputTextModule,
    DataVoidComponent,
  ],
  templateUrl: './memorando-apoio-defesa-monografia.component.html',
  styleUrls: ['./memorando-apoio-defesa-monografia.component.scss'],
  providers: [
    ApiService,
    BolseiroService,
    BolsaService,
    InstituicaoEnsinoService,
  ],
})
export class MemorandoApoioDefesaMonografiaComponent
  implements OnInit, OnDestroy
{
  @Output() formValueChanges = new EventEmitter<{
    data: any;
    valid: boolean;
  }>();
  @Input() bolseiroID!: string;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() documento!: any;

  // services
  fb = inject(FormBuilder);
  bolsaService = inject(BolsaService);
  bolseiroService = inject(BolseiroService);
  instituicaoEnsinoService = inject(InstituicaoEnsinoService);

  // state
  form!: FormGroup;
  pagination: Pagination<any> = new Pagination<any>();
  estudantes: any[] = [];
  InstEnsino: any[] = [];
  bolsa: any;
  valorRegra: string | null = null;

  private destroy$ = new Subject<void>();

  // ---------------- lifecycle ----------------
  ngOnInit(): void {
    this.buildForm();
    this.wireCoreChanges();
    // this.getAllBolseiros();
    this.getAllInstituicoesEnsino();
    // this.syncDisabled();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- form ----------------
  private buildForm(): void {
    this.form = this.fb.group({
      instituicao: ['', Validators.required],
      estudanteID: ['', Validators.required],
      bolsaID: ['', Validators.required],
      valores: [{ value: '', disabled: true }, Validators.required],
      moeda: [{ value: '', disabled: false }],
      valoresextenso: [{ value: '', disabled: true }, Validators.required],
      idioma: ['pt', Validators.required],
    });
  }

  private syncDisabled(): void {
    if (!this.form) return;
    this.disabled
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  // ---------------- subscriptions / wiring ----------------
  private wireCoreChanges(): void {
    // emissão centralizada
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitFormValues());
    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitFormValues());

    // estudante mudou → reset + carregar bolsa + regra
    this.form
      .get('estudanteID')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.resetBolsaCampos();
        if (!value) return;
        this.loadAllBolsa({ BolseiroID: value });
      });

    // bolsa trocada manualmente (radio/tabela) → actualiza id
    this.form
      .get('bolsaID')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitFormValues());

    this.form
      .get('instituicao')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.resetBolsaCampos();
        this.estudantes = [];
        if (!value) return;
        this.getAllBolseiros({ instituicaoEnsinoID: value });
      });
  }

  private resetBolsaCampos(): void {
    this.bolsa = null;
    this.valorRegra = null;
    this.form.patchValue(
      { bolsaID: '', valores: '', valoresextenso: '', moeda: '' },
      { emitEvent: true }
    );
    this.pagination = new Pagination<any>().deserialize([]);
  }

  // ---------------- UI helpers ----------------
  get f() {
    return this.form.controls;
  }

  onPageChange(event: any): void {
    this.pagination.pageNumber = (event?.page ?? 0) + 1;
    this.pagination.pageSize = event?.rows ?? 5;
    this.loadAllBolsa({ BolseiroID: this.form.get('estudanteID')?.value });
  }

  onRadioChange(event: any): void {
    this.form.get('bolsaID')?.setValue(event?.id ?? null, { emitEvent: true });
  }

  getSeverity(status: string) {
    switch (status) {
      case 'Activo':
      case 'Finalizado':
        return 'success';
      case 'Inactivo':
        return 'warn';
      case 'Suspenso':
        return 'danger';
      case 'Pendente':
        return 'info';
      default:
        return 'warn';
    }
  }

  // ---------------- data ----------------
  getAllInstituicoesEnsino(): void {
    this.instituicaoEnsinoService
      .list({ pageSize: 100, PaisID: '193' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (institutions) => (this.InstEnsino = institutions.data || []),
        error: (e) => console.error('Erro ao carregar instituições:', e),
      });
  }

  getAllBolseiros(queryParams: any = {}): void {
    this.bolseiroService
      .list({ ...queryParams, pageSize: 100, OrderBy: 'id desc' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (bolseiros) => (this.estudantes = bolseiros.data || []),
        error: (e) => console.error('Erro ao carregar bolseiros:', e),
      });
  }

  loadAllBolsa(queryParams: any = {}): void {
    const params = {
      ...queryParams,
      pageNumber: this.pagination.pageNumber ?? 1,
      pageSize: this.pagination.pageSize ?? 5,
    };
    this.bolsaService
      .list({
        ...params,
        BolseiroID: queryParams.BolseiroID || this.bolseiroID,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.bolsa = response?.data?.[0];
          this.pagination = new Pagination<any>().deserialize(response);
          this.form
            .get('bolsaID')
            ?.setValue(this.bolsa?.id ?? '', { emitEvent: true });
          this.fetchValorByRegra(this.bolsa);
        },
        error: (e) => console.error('Erro ao carregar bolsa:', e),
      });
  }

  // ---------------- regra/valor ----------------
  private pickMatricula(b: any) {
    const arr = Array.isArray(b?.matricula) ? b.matricula : [];
    if (!arr.length) return null;

    const emCurso = arr.find(
      (m: any) => (m?.estadoDescricao || '').toLowerCase() === 'em curso'
    );
    if (emCurso) return emCurso;

    const parse = (d: any) => (d ? new Date(d).getTime() : 0);
    return (
      arr
        .slice()
        .sort(
          (a: any, c: any) => parse(c?.dataInicio) - parse(a?.dataInicio)
        )[0] || null
    );
  }

  private buildRegraPayload(bolsa: any): VerificarRegraPagamentoPayload {
    const matricula = this.pickMatricula(bolsa);
    const anoCurricular = matricula?.anoCurricular ?? bolsa?.anoCurricular ?? 1;

    const programaFormacaoID =
      bolsa?.programaFormacaoID ??
      bolsa?.areaProgramaFormacao?.programaFormacaoId ??
      bolsa?.areaProgramaFormacao?.programaFormacaoID ??
      0;

    const bolseiroID = Number(bolsa?.bolseiroID ?? bolsa?.bolseiro?.id ?? 0);

    return {
      id: '0',
      pedidoID: Number(bolsa?.pedidoID ?? 0),
      bolseiroID,
      requerenteID: Number(bolsa?.requerenteID ?? 0),
      isActive: Boolean(bolsa?.isActive ?? true),
      tipoBolsa: Number(bolsa?.tipoBolsa ?? 0),
      paisID: Number(bolsa?.paisID ?? 0),
      instituicaoGestoraID: Number(
        bolsa?.instituicaoGestoraID ?? bolsa?.instituicaoGestora?.id ?? 0
      ),
      nivelAcademico: Number(bolsa?.nivelAcademico ?? 0),
      areaProgramaFormacaoID: Number(
        bolsa?.areaProgramaFormacaoID ?? bolsa?.areaProgramaFormacao?.id ?? 0
      ),
      programaFormacaoID: Number(programaFormacaoID),
      anoCurricularFrequencia: String(anoCurricular),
      valorSubsidio: Number(bolsa?.valorSubsidio ?? 0),
    };
  }

  private fetchValorByRegra(bolsa: any): void {
    if (!bolsa) {
      this.form.patchValue(
        { valores: '', moeda: '', valoresextenso: '' },
        { emitEvent: true }
      );
      this.valorRegra = null;
      this.emitFormValues();
      return;
    }

    const body = this.buildRegraPayload(bolsa);
    this.bolsaService
      .verificarRegraPagamento(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          let valor = Number(res?.valorPagamento ?? 0);
          let moeda = this.form.get('moeda')?.value ?? '';

          if (!valor || Number.isNaN(valor)) {
            const lista = Array.isArray(res?.regraPagamentoSubsidio)
              ? res.regraPagamentoSubsidio
              : [];
            const monografia =
              lista.find((x: any) => x?.subsidioId === 10) ??
              lista.find((x: any) =>
                String(x?.subsidios?.descricao ?? '')
                  .toLowerCase()
                  .includes('apoio a monografia')
              );

            valor = Number(monografia?.valor ?? 0);
            moeda = monografia?.moeda ?? '';
          }

          const valorFmt = this.formatPtBR(valor || 0);
          const valorExt = valor > 0 ? toExtensoComMoeda(valor, moeda) : '';

          this.valorRegra = valor > 0 ? valorFmt : null;

          this.form.patchValue(
            { valores: valorFmt, moeda, valoresextenso: valorExt },
            { emitEvent: true }
          );
        },
        error: () => {
          this.valorRegra = null;
          this.form.patchValue(
            { valores: '', moeda: '', valoresextenso: '' },
            { emitEvent: true }
          );
        },
      });
  }

  // ---------------- utils ----------------
  private formatPtBR(n: number): string {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n || 0);
  }

  private emitFormValues(): void {
    const data = {
      ...this.form.getRawValue(),
      bolsaID: this.form.get('bolsaID')?.value,
      valores: String(this.form.get('valores')?.value ?? ''),
      valoresextenso: this.form.get('valoresextenso')?.value,
      // tipoDocumento: 5, // <- ajuste conforme o teu catálogo
    };
    this.formValueChanges.emit({ data, valid: this.form.valid });
  }
}
